import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  GlobalStoreService,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  NgCommonModule,
  UserIconComponent,
} from '@principle-theorem/ng-shared';
import { Brand } from '@principle-theorem/principle-core';
import {
  IReferral,
  IStaffer,
  MentionResourceType,
  ReferralActor,
  isStafferActor,
  type ITeam,
} from '@principle-theorem/principle-core/interfaces';
import {
  WithRef,
  filterUndefined,
  isSameIdentifier,
  toNamedTypeDocument,
  type DocumentReference,
} from '@principle-theorem/shared';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ReferralService } from '../../services/referral.service';

interface IFollowerDisplay {
  ref: WithRef<IStaffer>['ref'];
  name: string;
}

@Component({
  selector: 'pr-referral-followers',
  imports: [
    NgCommonModule,
    MatBadgeModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatMenuModule,
    MatSlideToggleModule,
    MatDividerModule,
    UserIconComponent,
  ],
  templateUrl: './referral-followers.component.html',
  styleUrl: './referral-followers.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralFollowersComponent {
  private _referralService = inject(ReferralService);
  private _organisation = inject(OrganisationService);
  private _globalStore = inject(GlobalStoreService);

  referral = input.required<WithRef<IReferral>>();
  currentUser = input<ReferralActor | undefined>();

  searchQuery = signal('');
  followers = signal<IFollowerDisplay[]>([]);
  allStaff = signal<WithRef<IStaffer>[]>([]);
  allTeams = signal<WithRef<ITeam>[]>([]);

  followerCount = computed(() => this.referral().followers.length);

  isCurrentUserFollowing = computed(() => {
    const user = this.currentUser();
    if (!user || !isStafferActor(user)) {
      return false;
    }
    const refs = this.referral().followers;
    return refs.some((ref) => isSameIdentifier(ref, user));
  });

  isCurrentUserStaffer = computed(() => {
    const user = this.currentUser();
    return !!user && isStafferActor(user);
  });

  filteredFollowers = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) {
      return this.followers();
    }
    return this.followers().filter((follower) =>
      follower.name.toLowerCase().includes(query)
    );
  });

  availableStaff = computed(() => {
    const followerRefs = this.referral().followers;
    return this.allStaff().filter(
      (staffer) => !followerRefs.some((ref) => isSameIdentifier(ref, staffer))
    );
  });

  filteredStaff = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) {
      return this.availableStaff();
    }
    return this.availableStaff().filter((staffer) =>
      staffer.user.name.toLowerCase().includes(query)
    );
  });

  filteredTeams = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) {
      return this.allTeams();
    }
    return this.allTeams().filter((team) =>
      team.name.toLowerCase().includes(query)
    );
  });

  constructor() {
    this._organisation.staff$
      .pipe(takeUntilDestroyed())
      .subscribe((staff) => this.allStaff.set(staff));

    this._organisation.brand$
      .pipe(
        filterUndefined(),
        switchMap((brand) => Brand.teams$(brand)),
        takeUntilDestroyed()
      )
      .subscribe((teams) => this.allTeams.set(teams));

    effect(() => {
      this.referral();
      void this._loadFollowers();
    });
  }

  async toggleFollow(): Promise<void> {
    const user = this.currentUser();
    const referral = this.referral();
    if (!user || !isStafferActor(user)) {
      return;
    }
    const staffer = user;
    if (this.isCurrentUserFollowing()) {
      await this._referralService.unfollowReferral(referral, staffer);
      return;
    }
    await this._referralService.followReferral(referral, staffer);
  }

  async addFollower(staffer: WithRef<IStaffer>): Promise<void> {
    const referral = this.referral();
    const existingFollowers = referral.followers;
    if (existingFollowers.some((ref) => isSameIdentifier(ref, staffer))) {
      return;
    }
    const stafferActor = toNamedTypeDocument(
      staffer,
      MentionResourceType.Staffer
    );
    await this._referralService.followReferral(referral, stafferActor);
  }

  async removeFollower(follower: IFollowerDisplay): Promise<void> {
    const referral = this.referral();
    const staffer = this.allStaff().find((current) =>
      isSameIdentifier(current, { ref: follower.ref })
    );
    if (!staffer) {
      // eslint-disable-next-line no-console
      console.warn(`Could not find staffer for follower: ${follower.ref.path}`);
      return;
    }
    const stafferActor = toNamedTypeDocument(
      staffer,
      MentionResourceType.Staffer
    );
    await this._referralService.unfollowReferral(referral, stafferActor);
  }

  async addTeamFollowers(team: WithRef<ITeam>): Promise<void> {
    const teamMembers = this.allStaff().filter((staffer) =>
      (staffer.teams ?? []).some(
        (stafferTeam) => stafferTeam.ref.path === team.ref.path
      )
    );
    for (const member of teamMembers) {
      await this.addFollower(member);
    }
  }

  onMenuKeydown(event: KeyboardEvent): void {
    if (event.target instanceof HTMLInputElement) {
      event.stopPropagation();
    }
  }
  onSearchInput(event: Event): void {
    const element = event.target as HTMLInputElement;
    this.searchQuery.set(element.value);
  }

  getStafferImage$(
    stafferRef: DocumentReference<IStaffer>
  ): Observable<string | undefined> {
    return this._globalStore.getStafferImage$({ ref: stafferRef });
  }

  private _loadFollowers(): void {
    const refs = this.referral().followers;
    const cachedStaff = this.allStaff();

    const resolved: IFollowerDisplay[] = refs.map((ref) => {
      const staffer = cachedStaff.find((current) =>
        isSameIdentifier(current, { ref })
      );
      return {
        ref,
        name: staffer?.user.name ?? 'Unknown',
      };
    });

    this.followers.set(resolved);
  }
}
